/**
 * scenario-start-ux (Phase 4, Task 2.3) ScenarioIntroOverlay 本体。
 *
 * 初回 scene 生成期間 (POST /playlines + AI 生成、1-15 秒) を段階演出でカバーする overlay。
 * performAction 経路の LoadingOverlay は無変更 (Req 7.2)、本 overlay は初回専用。
 *
 * 機能:
 *   - Feature flag off で LoadingOverlay に delegate (Req 8.3 bit-by-bit 互換)
 *   - flag on + scenario 取得済で title → worldview → closing 段階表示
 *   - scenario 未取得 (pending / error) で「生成中です…」fallback
 *   - 開始時に登場人物は出さず、人物の登場は本編側へ委ねる
 *   - 長引き補助文言 (既存 `longGenerationNoticeMs` 踏襲、Req 3.1-3.3)
 *   - `isPending=false` 遷移で 300ms fade out (Req 1.2)
 *   - `prefers-reduced-motion` で transition 無効化
 *   - `role="status"` + `aria-live="polite"` (Req 6.1)
 */

import { useEffect, useMemo, useState } from "react";
import { loadClientEnv } from "../../config/env";
import { LoadingOverlay } from "./LoadingOverlay";
import {
  ClosingStage,
  TitleStage,
  WorldviewStage,
} from "./ScenarioIntroStages";
import { useScenarioStartStage } from "./useScenarioStartStage";
import type {
  ScenarioIntroOverlayProps,
  StageId,
} from "./scenario-intro.types";

const FADE_MS = 300;

export function ScenarioIntroOverlay(
  props: ScenarioIntroOverlayProps,
): JSX.Element | null {
  const { isPending, scenario, scenarioErrored, noticeDelayMs, stageDurations } =
    props;
  const env = useMemo(() => loadClientEnv(), []);

  // Feature flag off: 従来 LoadingOverlay に delegate (Req 8.3)
  if (!env.scenarioStartUxEnabled) {
    const delegateProps =
      noticeDelayMs !== undefined ? { isPending, noticeDelayMs } : { isPending };
    return <LoadingOverlay {...delegateProps} />;
  }

  return (
    <ScenarioIntroOverlayBody
      isPending={isPending}
      scenario={scenario}
      scenarioErrored={scenarioErrored}
      noticeDelayMs={noticeDelayMs ?? env.longGenerationNoticeMs}
      stageDurations={stageDurations}
    />
  );
}

function ScenarioIntroOverlayBody(props: {
  isPending: boolean;
  scenario?: ScenarioIntroOverlayProps["scenario"];
  scenarioErrored?: boolean | undefined;
  noticeDelayMs: number;
  stageDurations?: ScenarioIntroOverlayProps["stageDurations"];
}): JSX.Element | null {
  const { isPending, scenario, scenarioErrored, noticeDelayMs, stageDurations } =
    props;

  // fade out 管理: isPending=false で 300ms 後に DOM から消す
  const [visible, setVisible] = useState<boolean>(isPending);
  useEffect(() => {
    if (isPending) {
      setVisible(true);
      return;
    }
    const id = window.setTimeout(() => setVisible(false), FADE_MS);
    return () => window.clearTimeout(id);
  }, [isPending]);

  // 長引き補助文言タイマー
  const [longRunning, setLongRunning] = useState<boolean>(false);
  useEffect(() => {
    if (!isPending) {
      setLongRunning(false);
      return;
    }
    const id = window.setTimeout(() => setLongRunning(true), noticeDelayMs);
    return () => window.clearTimeout(id);
  }, [isPending, noticeDelayMs]);

  // scenario 取得完了で stage タイマー起動
  const scenarioReady = scenario !== undefined && !scenarioErrored;
  const stageHook = useScenarioStartStage(
    stageDurations !== undefined
      ? { scenarioReady, stageDurations }
      : { scenarioReady },
  );
  const { stage, reducedMotion } = stageHook;

  if (!visible) return null;

  const fadeClass =
    isPending || reducedMotion
      ? "opacity-100"
      : "opacity-0 transition-opacity duration-300 motion-reduce:transition-none";

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="scenario-intro-overlay"
      data-stage={stage}
      className={`fixed inset-0 z-50 flex flex-col items-center justify-center gap-6 bg-reader-overlay p-6 ${fadeClass}`}
    >
      {scenario === undefined || scenarioErrored ? (
        <ScenarioFallback
          scenarioErrored={scenarioErrored === true}
          longRunning={longRunning}
        />
      ) : (
        <>
          {/* Additive 表示: 現在 stage までの content を重ね続ける (Req 2.7 継続性) */}
          {shouldRenderStage(stage, "title") && (
            <TitleStage scenario={scenario} reducedMotion={reducedMotion} />
          )}
          {shouldRenderStage(stage, "worldview") && (
            <WorldviewStage
              scenario={scenario}
              reducedMotion={reducedMotion}
            />
          )}
          {shouldRenderStage(stage, "closing") && (
            <ClosingStage
              scenario={scenario}
              reducedMotion={reducedMotion}
              longRunning={longRunning}
            />
          )}
        </>
      )}
    </div>
  );
}

const STAGE_ORDER: readonly StageId[] = [
  "title",
  "worldview",
  "closing",
] as const;

function shouldRenderStage(current: StageId, target: StageId): boolean {
  return STAGE_ORDER.indexOf(target) <= STAGE_ORDER.indexOf(current);
}

function ScenarioFallback(props: {
  scenarioErrored: boolean;
  longRunning: boolean;
}): JSX.Element {
  return (
    <div className="flex flex-col items-center gap-2">
      <p className="text-reader-primary text-lg">
        {props.scenarioErrored ? "物語を準備できませんでした" : "物語を準備しています…"}
      </p>
      {props.longRunning && !props.scenarioErrored && (
        <p
          className="text-amber-300 text-sm"
          data-testid="scenario-intro-long-running-fallback"
        >
          生成に時間がかかっています…
        </p>
      )}
    </div>
  );
}
